// Shared formatting helpers for drop UI surfaces. Kept tiny + side-effect-free
// so they're cheap to import from cards, calendar, detail page, and homepage
// strip alike.

export const RELEASE_TYPES = {
  genetic_drop: { label: "Genetic Drop",    color: "#7FCB99", bg: "rgba(79,168,111,0.12)" },
  restock:      { label: "Restock",         color: "#D4AF37", bg: "rgba(212,175,55,0.12)" },
  collab:       { label: "Collab",          color: "#C28FFF", bg: "rgba(194,143,255,0.14)" },
  merch:        { label: "Merch",           color: "#FFB07A", bg: "rgba(255,176,122,0.14)" },
  auction_event:{ label: "Auction Event",   color: "#8FB3FF", bg: "rgba(143,179,255,0.14)" },
  preview_event:{ label: "Preview Event",   color: "#A8AEB8", bg: "rgba(255,255,255,0.06)" },
};

export const STATUS_STYLES = {
  scheduled: { label: "Upcoming", color: "#8FB3FF", bg: "rgba(143,179,255,0.12)" },
  live:      { label: "Live now", color: "#7FCB99", bg: "rgba(79,168,111,0.14)" },
  archived:  { label: "Archived", color: "#7A8190", bg: "rgba(255,255,255,0.04)" },
  draft:     { label: "Draft",    color: "#A8AEB8", bg: "rgba(255,255,255,0.04)" },
};

export function releaseMeta(d) {
  return RELEASE_TYPES[d?.release_type] || RELEASE_TYPES.genetic_drop;
}

export function statusMeta(d) {
  const s = d?.status || "draft";
  if (s === "scheduled" || (s === "live" && d?.scheduled_at && new Date(d.scheduled_at) > new Date())) {
    return STATUS_STYLES.scheduled;
  }
  return STATUS_STYLES[s] || STATUS_STYLES.draft;
}

export function dropHref(d) {
  if (!d) return "/new-drops";
  return `/drop/${d.slug || d.id}`;
}

// Granular countdown — returns null past the target. Used by the cinematic
// hero (3-segment chunks) AND by tighter inline strips (compact mode).
export function countdownParts(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return { live: true, days: 0, hours: 0, minutes: 0, seconds: 0, total_ms: 0 };
  const total = Math.floor(ms / 1000);
  return {
    live: false,
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
    total_ms: ms,
  };
}

export function formatCountdownInline(iso) {
  const c = countdownParts(iso);
  if (!c) return "TBD";
  if (c.live) return "LIVE NOW";
  if (c.days > 0) return `${c.days}d ${c.hours}h ${c.minutes}m`;
  return `${String(c.hours).padStart(2,"0")}:${String(c.minutes).padStart(2,"0")}:${String(c.seconds).padStart(2,"0")}`;
}

export function formatDropDate(iso) {
  if (!iso) return "Date TBD";
  const d = new Date(iso);
  return d.toLocaleString([], {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// Add-to-calendar helpers ----------------------------------------------------
// Format a JS Date → "YYYYMMDDTHHMMSSZ" UTC stamp for Google Calendar URLs.
function _gcalStamp(d) {
  return d.toISOString().replace(/[-:]|\.\d{3}/g, "");
}

/**
 * Build a Google Calendar event URL for a drop. Opens in a new tab so users
 * can save the launch with one click. Falls back to "now" if the drop has no
 * scheduled_at; the user can still edit the time before saving.
 */
export function googleCalendarUrl(drop) {
  if (!drop) return "#";
  const start = drop.scheduled_at ? new Date(drop.scheduled_at) : new Date();
  const end = new Date(start.getTime() + 90 * 60 * 1000); // 90-minute default
  const title = `[Drop] ${drop.title || "CannaForge launch"}`;
  const details = [
    `${drop.store_name || "CannaForge"} · ${drop.title || ""}`,
    drop.subtitle || "",
    drop.description || "",
  ].filter(Boolean).join("\n\n");
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/drop/${drop.slug || drop.id}`
    : "";
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${_gcalStamp(start)}/${_gcalStamp(end)}`,
    details: details + (url ? `\n\nDetails: ${url}` : ""),
    location: url,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Build an absolute URL to the backend .ics export for a drop. The endpoint
 * returns an RFC 5545 calendar file with Content-Disposition: attachment so
 * clicking the link triggers a "Save to Calendar" prompt on most devices.
 */
export function icsExportUrl(drop) {
  if (!drop) return "#";
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/api/drops/${drop.slug || drop.id}/calendar.ics`;
}
